/* eslint-disable @microsoft/sdl/react-iframe-missing-sandbox */
import { getDefaultImageAspectRatio } from '@app/ducks/helpers';
import { getIsImageMedia, getIsVideoMedia } from '@app/services/media';
import { useIsOverflowing } from '@app/utils/hooks/useIsOverflowing';
import { AnyLightboxMedia } from '@typings/internal';
import React from 'react';
import { useSelector } from 'react-redux';

import { isDefined } from '../../../utils/is';
import { Button } from '../Button';
import { Placeholder } from '../CustomImage/Placeholder';
import { ChevronDown, ChevronUp } from '../Icons';

import { VimeoThumbnail } from './VimeoThumbnail';

interface Props {
  medias: AnyLightboxMedia[];
  currentIndex: number;
  onChange: (index: number) => void;
}

export const LightboxThumbnails = ({ medias, onChange, currentIndex }: Props) => {
  const aspectRatio = useSelector(getDefaultImageAspectRatio);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isOverflowing = useIsOverflowing(containerRef, { heightOnly: true });
  const thumbnailRefs = React.useRef<HTMLButtonElement[]>([]);

  const scrollUp = () => {
    const prevIndex = Math.max(currentIndex - 1, 0);
    onChange(prevIndex);
    thumbnailRefs.current[prevIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const scrollDown = () => {
    const nextIndex = Math.min(currentIndex + 1, thumbnailRefs.current.length - 1);
    onChange(nextIndex);
    thumbnailRefs.current[nextIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  React.useEffect(() => {
    thumbnailRefs.current[currentIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [currentIndex]);

  return (
    <div className="absolute bottom-24 left-4 top-28 z-overlay hidden w-20 lg:flex">
      {isOverflowing && (
        <>
          <div className="z-10 absolute -top-11 left-0 right-0 flex justify-center">
            <Button icon={ChevronUp} color="secondary" className="shadow-md" onClick={scrollUp} disabled={currentIndex === 0} />
          </div>
          <div className="z-10 absolute -bottom-11 left-0 right-0 flex justify-center">
            <Button
              icon={ChevronDown}
              color="secondary"
              className="shadow-md"
              onClick={scrollDown}
              disabled={currentIndex === medias.length - 1}
            />
          </div>
        </>
      )}

      <div ref={containerRef} className="-m-2 flex max-h-full flex-1 flex-col items-center gap-1 overflow-y-auto p-2 pr-1 no-scrollbar">
        {medias.map((media, index) => (
          <button
            ref={el => {
              if (el) thumbnailRefs.current[index] = el;
            }}
            key={`${media}-${index}`}
            className="relative w-full shrink-0 overflow-hidden rounded-regular"
            onClick={() => onChange(index)}
            style={{ aspectRatio: 1 / aspectRatio }}
          >
            <span className="h-full w-full">
              {getIsImageMedia(media) && !media.isPlaceholder && (
                <img src={media.src} className="h-full w-full object-cover" alt={media.title} />
              )}
              {getIsImageMedia(media) && media.isPlaceholder && (
                <Placeholder aspectRatio={aspectRatio} className="h-full w-full object-cover" shouldShowIcon />
              )}
              {getIsVideoMedia(media) && isDefined(media.src) && <VimeoThumbnail src={media.src} />}
            </span>
            {index !== currentIndex && <span className="pointer-events-none absolute inset-0 bg-white/50" />}
          </button>
        ))}
      </div>
    </div>
  );
};
